import React, { createContext, useReducer, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { logger } from '@/utils/logger'
import {
  type BatchExecutionStatus,
  type AnalysisItemStatus,
  isCompletedStatus,
} from '@/types/analysis/batch-status'
import { useWebSocket } from '@/contexts/websocket-context'
import type { BatchProgressEventData, BatchStatusEventData } from '@/types/websocket'
import { getBatchProgressBulk } from '@/api/requests/analysis-batch'
import { useAuth } from '@/hooks/common/useAuth'
import { BATCH_CONFIG } from '@/config/batch'
import { toast } from '@/components/common/ui/use-toast'

/**
 * 分析メトリクス更新のバリデーション関数
 * 不正な変動（Task Count <-> Analysis Count の混在）を検出
 */
function validateAnalysisMetricsUpdate(
  currentMetrics: BatchAnalysisProgress['analysisMetrics'] | undefined,
  incomingMetrics: NonNullable<BatchAnalysisProgress['analysisMetrics']>,
  batchId: string
): boolean {
  // 初回設定の場合は常に許可
  if (!currentMetrics) {
    logger.debug('[AnalysisMetricsValidation] Initial metrics setting - allowed', {
      batchId,
      incomingMetrics,
    })
    return true
  }

  const currentTotal = currentMetrics.totalAnalysesCount
  const incomingTotal = incomingMetrics.totalAnalysesCount

  // 🔧 CRITICAL: 総分析数の大幅な変動を検出
  if (currentTotal > 0 && incomingTotal > 0) {
    const changeRatio = Math.abs(currentTotal - incomingTotal) / currentTotal

    // 20%以上の変動は不正とみなす（Task Count <-> Analysis Count混在の典型パターン）
    if (changeRatio > 0.2) {
      logger.warn(
        '[AnalysisMetricsValidation] Significant total count change detected - likely invalid',
        {
          batchId,
          currentTotal,
          incomingTotal,
          changeRatio: Math.round(changeRatio * 100),
          threshold: '20%',
          note: 'This could indicate Task Count vs Analysis Count confusion',
        }
      )
      return false
    }
  }

  // 🔧 VALIDATION: 完了数が総数を超える場合は不正
  if (incomingMetrics.completedAnalysesCount > incomingMetrics.totalAnalysesCount) {
    logger.warn('[AnalysisMetricsValidation] Completed count exceeds total - invalid', {
      batchId,
      completed: incomingMetrics.completedAnalysesCount,
      total: incomingMetrics.totalAnalysesCount,
    })
    return false
  }

  // 🔧 VALIDATION: 完了数の大幅な後退は不正（進捗は基本的に前進のみ）
  if (
    currentMetrics.completedAnalysesCount > 0 &&
    incomingMetrics.completedAnalysesCount < currentMetrics.completedAnalysesCount * 0.8
  ) {
    logger.warn(
      '[AnalysisMetricsValidation] Large regression in completed count - likely invalid',
      {
        batchId,
        currentCompleted: currentMetrics.completedAnalysesCount,
        incomingCompleted: incomingMetrics.completedAnalysesCount,
        note: 'Progress should generally move forward, not backward',
      }
    )
    return false
  }

  logger.debug('[AnalysisMetricsValidation] Metrics update validation passed', {
    batchId,
    currentMetrics,
    incomingMetrics,
  })
  return true
}

// バッチ分析の進捗状態インターフェース
export interface BatchAnalysisProgress {
  concurrentExecutionId: string
  spaceId: string
  serviceId: string
  serviceName: string
  status: BatchExecutionStatus
  routeInfo?: 'small' | 'large'
  totalQuestions: number
  totalModels: number
  totalCombinations: number
  completedCount: number
  failedCount: number
  pendingCount: number
  percentage: number
  startedAt: Date
  estimatedCompletionAt?: Date
  error?: string

  // 🆕 分析単位のメトリクス（タスク単位と分離）
  analysisMetrics?: {
    totalAnalysesCount: number // 実行予定の総分析数（例: 65 = 5プロンプト×1モデル×13サービス）
    completedAnalysesCount: number // 完了した分析数
    failedAnalysesCount: number // 失敗した分析数
    pendingAnalysesCount: number // 待機中の分析数
    analysisProgressPercentage: number // 分析単位の進捗率
  }

  // 🆕 ジョブタイプ（手動実行か定期実行かの区別）
  jobType?: 'ANALYSIS' | 'SCHEDULED_ANALYSIS'

  // 詳細進捗（プロンプト×モデルマトリックス）
  matrix: Array<{
    questionIndex: number
    questionText: string
    modelProvider: string
    modelVersion: string | null
    modelDisplayName: string
    status: AnalysisItemStatus
    startedAt?: Date
    completedAt?: Date
    cancelledAt?: Date
    error?: string
    executionTimeMs?: number
  }>
}

// Context の状態型
export interface AnalysisProgressState {
  batches: Record<string, BatchAnalysisProgress>
  isVisible: boolean
  activeBatchId: string | null
  // Metadata for tracking side effects to run
  sideEffects: {
    saveToStorage: boolean
    eventsToDispatch: Array<{
      type: 'batchAnalysisStarted' | 'batchAnalysisCompleted'
      detail: Record<string, unknown>
    }>
    syncRequests: Array<{
      concurrentExecutionId: string
      spaceId: string
      delay: number
    }>
  }
}

// 🔧 localStorage用のシリアライズ可能な型
interface SerializableBatchProgress {
  concurrentExecutionId: string
  spaceId: string
  serviceId: string
  serviceName: string
  status: BatchExecutionStatus
  routeInfo?: 'small' | 'large'
  totalQuestions: number
  totalModels: number
  totalCombinations: number
  completedCount: number
  failedCount: number
  pendingCount: number
  percentage: number
  startedAt: string // ISO string
  estimatedCompletionAt?: string // ISO string
  error?: string // 🔧 失敗時のエラーメッセージを追加
  batchType?: string // 🔧 バッチタイプ（analysis, generation等）

  // 🆕 分析単位のメトリクス（タスク単位と分離）
  analysisMetrics?: {
    totalAnalysesCount: number // 実行予定の総分析数（例: 65 = 5プロンプト×1モデル×13サービス）
    completedAnalysesCount: number // 完了した分析数
    failedAnalysesCount: number // 失敗した分析数
    pendingAnalysesCount: number // 待機中の分析数
    analysisProgressPercentage: number // 分析単位の進捗率
  }

  matrix: Array<{
    questionIndex: number
    questionText: string
    modelProvider: string
    modelVersion: string | null
    modelDisplayName: string
    status: AnalysisItemStatus
    startedAt?: string // ISO string
    completedAt?: string // ISO string
    cancelledAt?: string // ISO string
    error?: string
    executionTimeMs?: number
  }>
}

// 🔧 localStorage キー
const BATCH_PROGRESS_STORAGE_KEY = 'analysis_batch_progress'

// 🔧 localStorage ヘルパー関数
const loadBatchProgressFromStorage = (): Record<string, BatchAnalysisProgress> => {
  try {
    const stored = localStorage.getItem(BATCH_PROGRESS_STORAGE_KEY)
    if (!stored) return {}

    const serialized: Record<string, SerializableBatchProgress> = JSON.parse(stored)
    const deserialized: Record<string, BatchAnalysisProgress> = {}

    Object.entries(serialized).forEach(([id, batch]) => {
      // 🔧 BACKWARD COMPATIBILITY: Set default batchType for legacy batches
      const batchType = batch.batchType || 'analysis'

      // 🔧 FILTER: Only restore analysis batches, ignore prompt generation batches
      if (batchType !== 'analysis') {
        logger.debug('[AnalysisProgressContext] Ignoring non-analysis batch during restoration:', {
          batchId: id,
          batchType,
        })
        return
      }

      // 🔧 古すぎるバッチは除外（24時間以上経過）
      const startedAt = new Date(batch.startedAt)
      const now = new Date()
      const hoursSinceStart = (now.getTime() - startedAt.getTime()) / (1000 * 60 * 60)

      if (hoursSinceStart < 24) {
        // 実行中のバッチまたは最近完了したバッチを復元
        //
        // CONFIRMING_START（開始確認中）は意図的に復元しない（D1-a）。
        // 離脱後に終局へ到達させる手段が無いため、復元すると同時実行数を占有し続ける。
        if (
          batch.status === 'RUNNING' ||
          batch.status === 'PENDING' ||
          ((batch.status === 'SUCCEEDED' ||
            batch.status === 'FAILED' ||
            batch.status === 'CANCELLED' ||
            batch.status === 'PARTIALLY_SUCCEEDED') &&
            hoursSinceStart < 0.083) // 5分以内 (5/60 hours)
        ) {
          deserialized[id] = {
            ...batch,
            startedAt: new Date(batch.startedAt),
            estimatedCompletionAt: batch.estimatedCompletionAt
              ? new Date(batch.estimatedCompletionAt)
              : undefined,
            matrix: batch.matrix.map(item => ({
              ...item,
              startedAt: item.startedAt ? new Date(item.startedAt) : undefined,
              completedAt: item.completedAt ? new Date(item.completedAt) : undefined,
              cancelledAt: item.cancelledAt ? new Date(item.cancelledAt) : undefined,
            })),
            // 🆕 分析メトリクスがない場合は初期値で補完
            analysisMetrics: batch.analysisMetrics || {
              totalAnalysesCount: batch.totalCombinations,
              completedAnalysesCount: batch.completedCount,
              failedAnalysesCount: batch.failedCount,
              pendingAnalysesCount: batch.pendingCount,
              analysisProgressPercentage: batch.percentage,
            },
          }
        }
      }
    })

    logger.debug('[AnalysisProgressContext] Loaded batch progress from localStorage:', {
      totalStored: Object.keys(serialized).length,
      restoredBatches: Object.keys(deserialized).length,
      batchIds: Object.keys(deserialized),
      batchStatuses: Object.values(deserialized).map(b => b.status),
    })

    return deserialized
  } catch (error) {
    logger.error(
      '[AnalysisProgressContext] Failed to load batch progress from localStorage:',
      error
    )
    return {}
  }
}

// 🔧 localStorage保存のデバウンス用
let saveToStorageTimeoutId: NodeJS.Timeout | null = null
const STORAGE_SAVE_DEBOUNCE_MS = 1000 // 1秒間隔で保存

const saveBatchProgressToStorage = (batches: Record<string, BatchAnalysisProgress>) => {
  // 既存のタイマーをクリア
  if (saveToStorageTimeoutId) {
    clearTimeout(saveToStorageTimeoutId)
  }

  // デバウンスして保存
  saveToStorageTimeoutId = setTimeout(() => {
    try {
      // 🔧 実行中および最近完了したバッチを保存（復元用に一時的に保持）
      const batchesToSave = Object.fromEntries(
        Object.entries(batches).filter(([_, batch]) => {
          // 実行中のバッチは常に保存
          if (batch.status === 'RUNNING' || batch.status === 'PENDING') {
            return true
          }

          // 完了・失敗・キャンセルされたバッチも30秒間は保持（リロード対応）
          if (
            batch.status === 'SUCCEEDED' ||
            batch.status === 'FAILED' ||
            batch.status === 'CANCELLED' ||
            batch.status === 'PARTIALLY_SUCCEEDED'
          ) {
            const now = new Date()
            // 完了時刻を正確に計算（matrix内の最新のcompletedAtを使用）
            let completionTime = batch.startedAt

            // matrix内の最新の完了時刻を探す
            batch.matrix.forEach(item => {
              if (item.completedAt && new Date(item.completedAt) > completionTime) {
                completionTime = new Date(item.completedAt)
              }
            })

            const secondsSinceCompletion = (now.getTime() - completionTime.getTime()) / 1000

            // 🔧 FIX: FAILEDバッチは短い保持時間にする（5秒）
            if (batch.status === 'FAILED' || batch.status === 'CANCELLED') {
              return secondsSinceCompletion < 5 // FAILED/CANCELLEDバッチは5秒のみ保持
            }
            return secondsSinceCompletion < 30 // その他は30秒保持
          }

          return false
        })
      )

      const serialized: Record<string, SerializableBatchProgress> = {}
      Object.entries(batchesToSave).forEach(([id, batch]) => {
        serialized[id] = {
          ...batch,
          startedAt: batch.startedAt.toISOString(),
          estimatedCompletionAt: batch.estimatedCompletionAt?.toISOString(),
          matrix: batch.matrix.map(item => ({
            ...item,
            startedAt: item.startedAt?.toISOString(),
            completedAt: item.completedAt?.toISOString(),
            cancelledAt: item.cancelledAt?.toISOString(),
          })),
          // 🆕 分析メトリクスも保存
          analysisMetrics: batch.analysisMetrics,
        }
      })

      localStorage.setItem(BATCH_PROGRESS_STORAGE_KEY, JSON.stringify(serialized))

      logger.debug('[AnalysisProgressContext] Saved batch progress to localStorage:', {
        totalBatches: Object.keys(batches).length,
        savedBatches: Object.keys(batchesToSave).length,
        savedBatchIds: Object.keys(batchesToSave),
      })
    } catch (error) {
      logger.error(
        '[AnalysisProgressContext] Failed to save batch progress to localStorage:',
        error
      )
    }
  }, STORAGE_SAVE_DEBOUNCE_MS)
}

// Action 型
export type AnalysisProgressAction =
  | {
      type: 'START_BATCH'
      payload: {
        concurrentExecutionId: string
        spaceId: string
        serviceId: string
        serviceName: string
        questions: Array<{ text: string; index: number }>
        models: Array<{
          provider: string
          version: string | null
          displayName: string
        }>
        competitorIds?: string[] // 🔧 競合他社IDを追加
        jobType?: 'ANALYSIS' | 'SCHEDULED_ANALYSIS' // 🆕 ジョブタイプ（手動実行か定期実行か）
        routeInfo?: 'small' | 'large'
      }
    }
  | {
      type: 'UPDATE_BATCH_PROGRESS'
      payload: {
        concurrentExecutionId: string
        completedCount: number
        failedCount: number
        // 🆕 APIからのステータス情報を反映（オプショナル）
        status?: 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'PARTIALLY_SUCCEEDED'
        // 🆕 分析単位のメトリクス（オプショナル）
        analysisMetrics?: {
          totalAnalysesCount: number
          completedAnalysesCount: number
          failedAnalysesCount: number
          pendingAnalysesCount: number
          analysisProgressPercentage: number
        }
      }
    }
  | {
      type: 'UPDATE_MATRIX_ITEM'
      payload: {
        concurrentExecutionId: string
        questionIndex: number
        modelProvider: string
        modelVersion: string | null
        status: AnalysisItemStatus
        error?: string
        executionTimeMs?: number
      }
    }
  | {
      type: 'COMPLETE_BATCH'
      payload: {
        concurrentExecutionId: string
      }
    }
  | {
      type: 'FAIL_BATCH'
      payload: {
        concurrentExecutionId: string
        error: string
      }
    }
  | {
      type: 'CONFIRM_BATCH_STARTED'
      payload: {
        concurrentExecutionId: string
      }
    }
  | {
      type: 'MARK_BATCH_INDETERMINATE'
      payload: {
        concurrentExecutionId: string
      }
    }
  | {
      type: 'CANCEL_BATCH'
      payload: {
        concurrentExecutionId: string
      }
    }
  | {
      type: 'REMOVE_BATCH'
      payload: {
        concurrentExecutionId: string
      }
    }
  | {
      type: 'SET_VISIBLE'
      payload: {
        visible: boolean
      }
    }
  | {
      type: 'SET_ACTIVE_BATCH'
      payload: {
        batchId: string | null
      }
    }
  | {
      type: 'RESTORE_FROM_STORAGE'
      payload: {
        batches: Record<string, BatchAnalysisProgress>
      }
    }
  | {
      type: 'SYNC_BATCH_STATUS'
      payload: {
        concurrentExecutionId: string
        actualStatus: BatchExecutionStatus
        completedAt?: Date
      }
    }
  | {
      type: 'CLEAR_SIDE_EFFECTS'
    }

// 🔧 初期状態（localStorage から復元）
const getInitialState = (): AnalysisProgressState => {
  // 🔧 初期化時に必ずlocalStorageから復元
  const restoredBatches = loadBatchProgressFromStorage()

  // アクティブなバッチ（実行中・待機中）を特定
  const activeBatchIds = Object.keys(restoredBatches).filter(
    id => restoredBatches[id].status === 'RUNNING' || restoredBatches[id].status === 'PENDING'
  )

  // 最近完了したバッチも考慮（リロード後の一時表示用）
  const recentlyCompletedBatchIds = Object.keys(restoredBatches).filter(id => {
    const batch = restoredBatches[id]
    return batch.status === 'SUCCEEDED' || batch.status === 'FAILED' || batch.status === 'CANCELLED'
  })

  // 優先順位: 実行中 > 待機中 > 最近完了
  const allRelevantBatchIds = [...activeBatchIds, ...recentlyCompletedBatchIds]

  // 最新の開始時刻を持つバッチをアクティブにする
  const latestActiveBatchId =
    allRelevantBatchIds.length > 0
      ? allRelevantBatchIds.reduce((latest, current) => {
          const latestBatch = restoredBatches[latest]
          const currentBatch = restoredBatches[current]

          // 実行中のバッチを優先
          if (latestBatch.status === 'RUNNING' && currentBatch.status !== 'RUNNING') return latest
          if (currentBatch.status === 'RUNNING' && latestBatch.status !== 'RUNNING') return current

          // 待機中のバッチを次に優先
          if (
            latestBatch.status === 'PENDING' &&
            !['RUNNING', 'PENDING'].includes(currentBatch.status)
          )
            return latest
          if (
            currentBatch.status === 'PENDING' &&
            !['RUNNING', 'PENDING'].includes(latestBatch.status)
          )
            return current

          // 同等の優先度の場合は開始時刻で判定
          return latestBatch.startedAt > currentBatch.startedAt ? latest : current
        })
      : null

  const shouldBeVisible = latestActiveBatchId !== null

  logger.debug('[AnalysisProgressContext] Initial state:', {
    restoredBatchCount: Object.keys(restoredBatches).length,
    activeBatchIds,
    recentlyCompletedBatchIds,
    latestActiveBatchId,
    shouldBeVisible,
    selectedBatchStatus: latestActiveBatchId ? restoredBatches[latestActiveBatchId].status : null,
  })

  return {
    batches: restoredBatches,
    isVisible: shouldBeVisible, // 🔧 関連するバッチがある場合は自動表示
    activeBatchId: latestActiveBatchId, // 🔧 最適なバッチを自動選択
    sideEffects: {
      saveToStorage: false,
      eventsToDispatch: [],
      syncRequests: [],
    },
  }
}

// Reducer 関数
function analysisProgressReducer(
  state: AnalysisProgressState,
  action: AnalysisProgressAction
): AnalysisProgressState {
  switch (action.type) {
    case 'RESTORE_FROM_STORAGE': {
      const { batches } = action.payload

      // アクティブなバッチ（実行中・待機中）を特定
      const activeBatchIds = Object.keys(batches).filter(
        id => batches[id].status === 'RUNNING' || batches[id].status === 'PENDING'
      )

      // 最近完了したバッチも考慮（リロード後の一時表示用）
      const recentlyCompletedBatchIds = Object.keys(batches).filter(id => {
        const batch = batches[id]
        return (
          batch.status === 'SUCCEEDED' || batch.status === 'FAILED' || batch.status === 'CANCELLED'
        )
      })

      // 優先順位: 実行中 > 待機中 > 最近完了
      const allRelevantBatchIds = [...activeBatchIds, ...recentlyCompletedBatchIds]

      // 最新の開始時刻を持つバッチをアクティブにする
      const latestActiveBatchId =
        allRelevantBatchIds.length > 0
          ? allRelevantBatchIds.reduce((latest, current) => {
              const latestBatch = batches[latest]
              const currentBatch = batches[current]

              // 実行中のバッチを優先
              if (latestBatch.status === 'RUNNING' && currentBatch.status !== 'RUNNING')
                return latest
              if (currentBatch.status === 'RUNNING' && latestBatch.status !== 'RUNNING')
                return current

              // 待機中のバッチを次に優先
              if (
                latestBatch.status === 'PENDING' &&
                !['RUNNING', 'PENDING'].includes(currentBatch.status)
              )
                return latest
              if (
                currentBatch.status === 'PENDING' &&
                !['RUNNING', 'PENDING'].includes(latestBatch.status)
              )
                return current

              // 同等の優先度の場合は開始時刻で判定
              return latestBatch.startedAt > currentBatch.startedAt ? latest : current
            })
          : null

      const shouldBeVisible = latestActiveBatchId !== null

      logger.debug('[AnalysisProgressReducer] RESTORE_FROM_STORAGE:', {
        batchCount: Object.keys(batches).length,
        activeBatchIds,
        recentlyCompletedBatchIds,
        latestActiveBatchId,
        shouldBeVisible,
        selectedBatchStatus: latestActiveBatchId ? batches[latestActiveBatchId].status : null,
        note: 'All restored batches now have batchType set for proper filtering',
      })

      return {
        ...state,
        batches,
        isVisible: shouldBeVisible, // 🔧 関連するバッチがある場合は表示
        activeBatchId: latestActiveBatchId, // 🔧 最適なバッチを選択
        sideEffects: {
          saveToStorage: false,
          eventsToDispatch: [],
          syncRequests: [],
        },
      }
    }

    case 'START_BATCH': {
      const {
        concurrentExecutionId,
        spaceId,
        serviceId,
        serviceName,
        questions,
        models,
        competitorIds = [], // 🔧 デフォルト値を設定
        jobType = 'ANALYSIS', // 🆕 デフォルトは手動実行
        routeInfo,
      } = action.payload

      const totalQuestions = questions.length
      const totalModels = models.length

      // 🔧 正しい総組み合わせ数の計算
      // 競合他社は同一分析内で処理されるため、総分析数に影響しない
      const totalCombinations = totalQuestions * totalModels

      logger.info('[AnalysisProgressContext] Starting batch:', {
        concurrentExecutionId,
        questionsCount: totalQuestions,
        modelsCount: totalModels,
        competitorCount: competitorIds.length,
        totalCombinations,
        calculationFormula: `${totalQuestions} × ${totalModels} = ${totalCombinations}`,
        note: 'Competitors are processed within the same analysis',
      })

      // マトリックスを初期化
      const matrix = questions.flatMap(question =>
        models.map(model => ({
          questionIndex: question.index,
          questionText: question.text,
          modelProvider: model.provider,
          modelVersion: model.version,
          modelDisplayName: model.displayName,
          status: 'PENDING' as const,
        }))
      )

      const newBatch: BatchAnalysisProgress = {
        concurrentExecutionId,
        spaceId,
        serviceId,
        serviceName,
        // 開始が確認できるまでは「実行中」を名乗らない（D1-a / FR-1-1）。
        // 登録自体は送信前に行い、進捗イベントの受信経路と同時実行数の計上は維持する。
        status: 'CONFIRMING_START',
        routeInfo,
        totalQuestions,
        totalModels,
        totalCombinations,
        completedCount: 0,
        failedCount: 0,
        pendingCount: totalCombinations,
        percentage: 0,
        startedAt: new Date(),
        matrix,

        // 🆕 分析メトリクスの初期値を設定
        analysisMetrics: {
          totalAnalysesCount: totalCombinations,
          completedAnalysesCount: 0,
          failedAnalysesCount: 0,
          pendingAnalysesCount: totalCombinations,
          analysisProgressPercentage: 0,
        },

        // 🆕 ジョブタイプ（手動実行か定期実行か）
        jobType,
      }

      logger.debug('[AnalysisProgressContext] Starting batch:', {
        concurrentExecutionId,
        totalCombinations,
        serviceName,
      })

      return {
        ...state,
        batches: {
          ...state.batches,
          [concurrentExecutionId]: newBatch,
        },
        isVisible: true,
        activeBatchId: concurrentExecutionId,
        sideEffects: {
          saveToStorage: true,
          eventsToDispatch: [
            {
              type: 'batchAnalysisStarted',
              detail: {
                batchId: concurrentExecutionId,
                spaceId,
                serviceId,
                serviceName,
                timestamp: new Date().toISOString(),
              },
            },
          ],
          syncRequests: [], // 🔧 WebSocket専用モード: sync requestsを無効化
        },
      }
    }

    case 'UPDATE_BATCH_PROGRESS': {
      const { concurrentExecutionId, completedCount, failedCount, status, analysisMetrics } =
        action.payload
      const batch = state.batches[concurrentExecutionId]

      if (!batch) {
        logger.warn(
          '[AnalysisProgressContext] Batch not found for progress update:',
          concurrentExecutionId
        )
        return state
      }

      // 🆕 分析メトリクスを処理（バリデーション付き）
      if (analysisMetrics && analysisMetrics.totalAnalysesCount > 0) {
        // 🔧 VALIDATION: 不正な分析メトリクス変動を検出
        const currentAnalysisMetrics = batch.analysisMetrics
        const isValidUpdate = validateAnalysisMetricsUpdate(
          currentAnalysisMetrics,
          analysisMetrics,
          concurrentExecutionId
        )

        if (!isValidUpdate) {
          logger.warn(
            '[AnalysisProgressContext] Invalid analysis metrics update detected - ignoring:',
            {
              concurrentExecutionId,
              currentMetrics: currentAnalysisMetrics,
              incomingMetrics: analysisMetrics,
              note: 'Protecting against metric fluctuations that could cause UI confusion',
            }
          )
          return state // 🔧 不正な更新は無視
        }

        logger.info(
          '[AnalysisProgressContext] Using validated analysis metrics for accurate counts:',
          {
            concurrentExecutionId,
            analysisMetrics,
            validationPassed: true,
            note: 'Using analysis-specific counts instead of task counts',
          }
        )

        const updatedBatch: BatchAnalysisProgress = {
          ...batch,
          // 既存のタスク単位カウンター（下位互換性のため保持）
          completedCount,
          failedCount,
          pendingCount: Math.max(0, batch.totalCombinations - completedCount - failedCount),
          percentage: Math.min(100, Math.round((completedCount / batch.totalCombinations) * 100)),
          // 🆕 APIからのステータス更新を反映
          status: status || batch.status,
          // 🆕 分析メトリクスを追加
          analysisMetrics,
        }

        return {
          ...state,
          batches: {
            ...state.batches,
            [concurrentExecutionId]: updatedBatch,
          },
          sideEffects: {
            saveToStorage: true,
            eventsToDispatch: [],
            syncRequests: [],
          },
        }
      }

      // 🔧 レガシー処理：分析メトリクスがない場合はタスク単位の従来処理
      const safeCompletedCount = Math.max(0, Math.min(completedCount, batch.totalCombinations))
      const safeFailedCount = Math.max(0, Math.min(failedCount, batch.totalCombinations))
      const safeTotalProcessed = safeCompletedCount + safeFailedCount

      // totalCombinations を超える場合は調整
      const adjustedCompletedCount =
        safeTotalProcessed > batch.totalCombinations
          ? Math.max(0, batch.totalCombinations - safeFailedCount)
          : safeCompletedCount
      const adjustedFailedCount =
        safeTotalProcessed > batch.totalCombinations
          ? Math.max(0, batch.totalCombinations - adjustedCompletedCount)
          : safeFailedCount

      const pendingCount = Math.max(
        0,
        batch.totalCombinations - adjustedCompletedCount - adjustedFailedCount
      )
      const percentage = Math.min(
        100,
        Math.round((adjustedCompletedCount / batch.totalCombinations) * 100)
      )

      // 🔧 異常な値の場合はログに記録
      if (
        completedCount > batch.totalCombinations ||
        failedCount > batch.totalCombinations ||
        completedCount + failedCount > batch.totalCombinations
      ) {
        logger.warn(
          '[AnalysisProgressContext] Invalid progress data received - adjusting values:',
          {
            concurrentExecutionId,
            originalCompletedCount: completedCount,
            originalFailedCount: failedCount,
            totalCombinations: batch.totalCombinations,
            adjustedCompletedCount,
            adjustedFailedCount,
            originalSum: completedCount + failedCount,
            adjustedSum: adjustedCompletedCount + adjustedFailedCount,
            calculatedPercentage: percentage,
            calculatedPendingCount: pendingCount,
          }
        )
      }

      // 推定完了時間を計算
      let estimatedCompletionAt: Date | undefined
      if (adjustedCompletedCount > 0) {
        const elapsedMs = Date.now() - batch.startedAt.getTime()
        const avgTimePerCompletion = elapsedMs / adjustedCompletedCount
        const remainingTime = avgTimePerCompletion * pendingCount
        estimatedCompletionAt = new Date(Date.now() + remainingTime)
      }

      const updatedBatch: BatchAnalysisProgress = {
        ...batch,
        completedCount: adjustedCompletedCount,
        failedCount: adjustedFailedCount,
        pendingCount,
        percentage,
        estimatedCompletionAt,
      }

      logger.debug('[AnalysisProgressContext] Updating batch progress:', {
        concurrentExecutionId,
        completedCount: adjustedCompletedCount,
        failedCount: adjustedFailedCount,
        percentage,
        pendingCount,
      })

      const newState = {
        ...state,
        batches: {
          ...state.batches,
          [concurrentExecutionId]: updatedBatch,
        },
      }

      // 🔧 localStorage に保存
      saveBatchProgressToStorage(newState.batches)

      return newState
    }

    case 'UPDATE_MATRIX_ITEM': {
      const {
        concurrentExecutionId,
        questionIndex,
        modelProvider,
        modelVersion,
        status,
        error,
        executionTimeMs,
      } = action.payload
      const batch = state.batches[concurrentExecutionId]

      if (!batch) {
        logger.warn(
          '[AnalysisProgressContext] Batch not found for matrix update:',
          concurrentExecutionId
        )
        return state
      }

      const updatedMatrix = batch.matrix.map(item => {
        if (
          item.questionIndex === questionIndex &&
          item.modelProvider === modelProvider &&
          item.modelVersion === modelVersion
        ) {
          const now = new Date()
          return {
            ...item,
            status,
            ...(status === 'RUNNING' && { startedAt: now }),
            ...(status === 'SUCCEEDED' && { completedAt: now }),
            ...(status === 'FAILED' && { completedAt: now }),
            ...(error && { error }),
            ...(executionTimeMs && { executionTimeMs }),
          }
        }
        return item
      })

      const updatedBatch: BatchAnalysisProgress = {
        ...batch,
        matrix: updatedMatrix,
      }

      logger.debug('[AnalysisProgressContext] Updating matrix item:', {
        concurrentExecutionId,
        questionIndex,
        modelProvider,
        status,
      })

      return {
        ...state,
        batches: {
          ...state.batches,
          [concurrentExecutionId]: updatedBatch,
        },
        sideEffects: {
          saveToStorage: true,
          eventsToDispatch: [],
          syncRequests: [],
        },
      }
    }

    case 'COMPLETE_BATCH': {
      const { concurrentExecutionId } = action.payload
      const batch = state.batches[concurrentExecutionId]

      if (!batch) {
        logger.warn(
          '[AnalysisProgressContext] Batch not found for completion:',
          concurrentExecutionId
        )
        return state
      }

      // 🔧 Race condition fix: Prevent duplicate completion calls
      if (
        batch.status === 'SUCCEEDED' ||
        batch.status === 'FAILED' ||
        batch.status === 'CANCELLED' ||
        batch.status === 'PARTIALLY_SUCCEEDED'
      ) {
        logger.warn(
          '[AnalysisProgressContext] Batch already completed - skipping duplicate completion:',
          {
            concurrentExecutionId,
            currentStatus: batch.status,
          }
        )
        return state
      }

      // 実際の完了・失敗数をmatrixから計算
      let completedItems = batch.matrix.filter(item => item.status === 'SUCCEEDED').length
      let failedItems = batch.matrix.filter(item => item.status === 'FAILED').length
      const pendingItems = batch.matrix.filter(item => item.status === 'PENDING').length
      const runningItems = batch.matrix.filter(item => item.status === 'RUNNING').length
      const totalItems = batch.matrix.length
      let updatedMatrix = batch.matrix

      // 🔧 詳細なmatrix状態をデバッグ出力
      console.log('[AnalysisProgressContext] 🔍 Matrix status details on completion:', {
        concurrentExecutionId,
        totalItems,
        completedItems,
        failedItems,
        pendingItems,
        runningItems,
        batchCurrentStatus: batch.status,
        analysisMetrics: batch.analysisMetrics,
        matrix: batch.matrix.map(item => ({
          question: item.questionIndex,
          questionText: item.questionText.substring(0, 50) + '...',
          model: item.modelProvider,
          modelVersion: item.modelVersion,
          status: item.status,
          error: item.error?.substring(0, 100),
          executionTimeMs: item.executionTimeMs,
        })),
      })

      // 🔧 改善された完了判定：WebSocketイベントからの完了通知を信頼する
      // バックエンドが完了と判断した場合は、フロントエンドも完了として扱う
      const hasUnprocessedItems = pendingItems > 0 || runningItems > 0

      // WebSocketイベントから完了通知が来た場合は、matrixの状態に関わらず完了処理を行う
      logger.info('[AnalysisProgressContext] 📊 Batch completion event received from WebSocket:', {
        concurrentExecutionId,
        matrixStatus: { completedItems, failedItems, pendingItems, runningItems },
        note: 'Trusting backend completion status - will force complete all items',
      })

      // 🔧 WebSocketから完了通知が来た場合は、全ての項目を強制的に完了状態にする
      if (pendingItems > 0 || runningItems > 0) {
        logger.info(
          '[AnalysisProgressContext] 🔧 Force completing all pending/running items due to WebSocket completion event:',
          {
            concurrentExecutionId,
            itemsToForceComplete: pendingItems + runningItems,
          }
        )

        updatedMatrix = batch.matrix.map(item => {
          if (item.status === 'PENDING' || item.status === 'RUNNING') {
            return {
              ...item,
              status: 'SUCCEEDED' as const,
              completedAt: new Date(),
              executionTimeMs: item.executionTimeMs || Date.now() - batch.startedAt.getTime(),
            }
          }
          return item
        })

        // カウントを再計算
        completedItems = updatedMatrix.filter(item => item.status === 'SUCCEEDED').length
        failedItems = updatedMatrix.filter(item => item.status === 'FAILED').length
      }

      // 🔧 未処理の項目がある場合は、適切なステータスに設定
      if (hasUnprocessedItems && !updatedMatrix) {
        logger.info('[AnalysisProgressContext] 🔧 Finalizing pending/running items:', {
          concurrentExecutionId,
          pendingItems,
          runningItems,
          note: 'Setting remaining items to appropriate final status',
        })

        updatedMatrix = batch.matrix.map(item => {
          if (item.status === 'PENDING' || item.status === 'RUNNING') {
            // 既に一部成功している場合は、残りも成功として扱う（サーバー側で処理済みの可能性）
            // 全て失敗している場合は、残りも失敗として扱う
            const shouldMarkAsCompleted = completedItems > 0 || failedItems === 0
            return {
              ...item,
              status: shouldMarkAsCompleted ? 'SUCCEEDED' : 'FAILED',
              error: shouldMarkAsCompleted
                ? undefined
                : item.error || 'Analysis completed without result',
              completedAt: new Date(),
              executionTimeMs: item.executionTimeMs || Date.now() - batch.startedAt.getTime(),
            }
          }
          return item
        })

        // 再計算
        const finalCompletedItems = updatedMatrix.filter(item => item.status === 'SUCCEEDED').length
        const finalFailedItems = updatedMatrix.filter(item => item.status === 'FAILED').length

        logger.info('[AnalysisProgressContext] 🔧 Final item counts after status update:', {
          concurrentExecutionId,
          originalCounts: { completedItems, failedItems, pendingItems, runningItems },
          finalCounts: { completed: finalCompletedItems, failed: finalFailedItems },
        })

        // カウントを更新
        completedItems = finalCompletedItems
        failedItems = finalFailedItems
      }

      // 最終的な処理アイテム数チェック
      const processedItems = completedItems + failedItems
      if (processedItems === 0) {
        logger.error('[AnalysisProgressContext] ❌ No items processed - marking as failed:', {
          concurrentExecutionId,
          totalItems,
          note: 'No completed or failed items found - this indicates a system error',
        })

        // すべての項目を失敗としてマーク
        updatedMatrix = batch.matrix.map(item => ({
          ...item,
          status: 'FAILED' as const,
          error: item.error || 'Analysis completed without result',
          completedAt: new Date(),
        }))

        const updatedBatch: BatchAnalysisProgress = {
          ...batch,
          status: 'FAILED',
          percentage: 0,
          completedCount: 0,
          failedCount: totalItems,
          matrix: updatedMatrix,
          error: 'All analyses failed to complete',
        }

        const newState = {
          ...state,
          batches: {
            ...state.batches,
            [concurrentExecutionId]: updatedBatch,
          },
        }

        saveBatchProgressToStorage(newState.batches)
        return newState
      }

      // 🔧 analysisMetricsを実際の結果に基づいて更新
      // バックエンドからの情報がある場合はそれを優先
      const updatedAnalysisMetrics = batch.analysisMetrics
        ? {
            ...batch.analysisMetrics,
            completedAnalysesCount: batch.analysisMetrics.totalAnalysesCount || completedItems,
            failedAnalysesCount: failedItems,
            pendingAnalysesCount: 0,
            analysisProgressPercentage: 100,
          }
        : {
            totalAnalysesCount: batch.totalCombinations,
            completedAnalysesCount: batch.totalCombinations,
            failedAnalysesCount: 0,
            pendingAnalysesCount: 0,
            analysisProgressPercentage: 100,
          }

      // 🔧 改善されたステータス判定ロジック
      let finalStatus: 'SUCCEEDED' | 'FAILED' | 'PARTIALLY_SUCCEEDED'

      if (completedItems === 0) {
        // 成功した項目が1つもない場合は完全失敗
        finalStatus = 'FAILED'
        logger.info('[AnalysisProgressContext] 📊 Final status: FAILED (no successful analyses)', {
          concurrentExecutionId,
          completedItems,
          failedItems,
          totalItems,
        })
      } else if (failedItems === 0) {
        // 失敗した項目が1つもない場合は完全成功
        finalStatus = 'SUCCEEDED'
        logger.info(
          '[AnalysisProgressContext] 📊 Final status: COMPLETED (all analyses successful)',
          {
            concurrentExecutionId,
            completedItems,
            failedItems,
            totalItems,
          }
        )
      } else {
        // 一部成功・一部失敗
        finalStatus = 'PARTIALLY_SUCCEEDED'
        logger.info(
          '[AnalysisProgressContext] 📊 Final status: PARTIALLY_SUCCEEDED (mixed results)',
          {
            concurrentExecutionId,
            completedItems,
            failedItems,
            totalItems,
            successRate: Math.round((completedItems / totalItems) * 100),
          }
        )
      }

      const updatedBatch: BatchAnalysisProgress = {
        ...batch,
        status: finalStatus,
        percentage: 100,
        completedCount: completedItems,
        failedCount: failedItems,
        pendingCount: 0, // 完了時は0
        matrix: updatedMatrix,
        analysisMetrics: updatedAnalysisMetrics,
      }

      logger.info('[AnalysisProgressContext] ✅ Batch completion processed successfully:', {
        concurrentExecutionId,
        finalStatus,
        finalCounts: {
          completed: completedItems,
          failed: failedItems,
          total: totalItems,
        },
        analysisMetrics: updatedAnalysisMetrics,
        percentage: 100,
        note: 'Batch marked as completed in progress context - will be removed in 5 seconds',
      })

      return {
        ...state,
        batches: {
          ...state.batches,
          [concurrentExecutionId]: updatedBatch,
        },
        sideEffects: {
          saveToStorage: true,
          eventsToDispatch: [
            {
              type: 'batchAnalysisCompleted',
              detail: {
                batchId: concurrentExecutionId,
                spaceId: batch.spaceId,
                serviceId: batch.serviceId,
                status: finalStatus,
                resultsCount: completedItems,
                timestamp: new Date().toISOString(),
              },
            },
          ],
          syncRequests: [], // 🔧 WebSocket専用モード: sync requestsを無効化
        },
      }
    }

    // 開始が確認できた（started 終局）。「開始確認中」から実行中へ昇格する（D1-a）
    case 'CONFIRM_BATCH_STARTED': {
      const { concurrentExecutionId } = action.payload
      const batch = state.batches[concurrentExecutionId]

      if (!batch) {
        logger.warn(
          '[AnalysisProgressContext] Batch not found for start confirmation:',
          concurrentExecutionId
        )
        return state
      }

      if (batch.status !== 'CONFIRMING_START') {
        // 既に昇格済み・終局済み。終局は1回だけ通知される前提だが、二重呼び出しでも壊さない
        return state
      }

      return {
        ...state,
        batches: {
          ...state.batches,
          [concurrentExecutionId]: { ...batch, status: 'RUNNING' },
        },
      }
    }

    // 開始を確認できなかった（indeterminate 終局）。
    // 打ち切りとして閉じる＝同時実行の計上から外れ、インジケータにも出ない。
    // 「失敗した」とは断定しない（FR-2-4）ため FAILED では閉じない（D1-a）。
    case 'MARK_BATCH_INDETERMINATE': {
      const { concurrentExecutionId } = action.payload
      const batch = state.batches[concurrentExecutionId]

      if (!batch) {
        logger.warn(
          '[AnalysisProgressContext] Batch not found for indeterminate close:',
          concurrentExecutionId
        )
        return state
      }

      if (batch.status === 'TIMEOUT') {
        return state
      }

      return {
        ...state,
        batches: {
          ...state.batches,
          [concurrentExecutionId]: { ...batch, status: 'TIMEOUT' },
        },
      }
    }

    case 'FAIL_BATCH': {
      const { concurrentExecutionId, error } = action.payload
      const batch = state.batches[concurrentExecutionId]

      if (!batch) {
        logger.warn('[AnalysisProgressContext] Batch not found for failure:', concurrentExecutionId)
        return state
      }

      // 🔧 既に失敗状態の場合は重複処理を防ぐ
      if (batch.status === 'FAILED') {
        logger.debug(
          '[AnalysisProgressContext] Batch already in FAILED state - skipping duplicate failure:',
          {
            concurrentExecutionId,
            currentError: batch.error,
            newError: error,
          }
        )
        return state // 状態変更なしで早期リターン
      }

      const updatedBatch: BatchAnalysisProgress = {
        ...batch,
        status: 'FAILED',
        error: error || 'Unknown error occurred',
        percentage: 0, // Reset to 0 to indicate failure
        // Keep completed/failed counts for diagnostic purposes
      }

      logger.error('[AnalysisProgressContext] Batch failed:', {
        concurrentExecutionId,
        error: error || 'No error message provided',
        originalStatus: batch.status,
        completedCount: batch.completedCount,
        failedCount: batch.failedCount,
      })

      return {
        ...state,
        batches: {
          ...state.batches,
          [concurrentExecutionId]: updatedBatch,
        },
        sideEffects: {
          saveToStorage: true,
          eventsToDispatch: [],
          syncRequests: [], // 🔧 WebSocket専用モード: sync requestsを無効化
        },
      }
    }

    case 'CANCEL_BATCH': {
      const { concurrentExecutionId } = action.payload
      const batch = state.batches[concurrentExecutionId]

      if (!batch) {
        logger.warn(
          '[AnalysisProgressContext] Batch not found for cancellation:',
          concurrentExecutionId
        )
        return state
      }

      // 完了済みのバッチはキャンセルできない
      if (
        batch.status === 'SUCCEEDED' ||
        batch.status === 'FAILED' ||
        batch.status === 'CANCELLED' ||
        batch.status === 'PARTIALLY_SUCCEEDED'
      ) {
        logger.info(
          '[AnalysisProgressContext] CANCEL_BATCH: Ignoring cancel for completed batch:',
          {
            concurrentExecutionId,
            status: batch.status,
          }
        )
        return state
      }

      // 🔧 analysisMetricsをキャンセル状態に更新（現在の進捗を保持）
      const updatedAnalysisMetrics = batch.analysisMetrics
        ? {
            ...batch.analysisMetrics,
            pendingAnalysesCount: 0, // キャンセル時は待機中をクリア
            // completedAnalysesCountとfailedAnalysesCountは現在の値を保持
          }
        : undefined

      const updatedBatch: BatchAnalysisProgress = {
        ...batch,
        status: 'CANCELLED',
        analysisMetrics: updatedAnalysisMetrics,
      }

      logger.info('[AnalysisProgressContext] Batch cancelled:', {
        concurrentExecutionId,
        analysisMetrics: updatedAnalysisMetrics,
      })

      return {
        ...state,
        batches: {
          ...state.batches,
          [concurrentExecutionId]: updatedBatch,
        },
        sideEffects: {
          saveToStorage: true,
          eventsToDispatch: [],
          syncRequests: [],
        },
      }
    }

    case 'REMOVE_BATCH': {
      const { concurrentExecutionId } = action.payload
      const { [concurrentExecutionId]: _removedBatch, ...remainingBatches } = state.batches

      logger.debug('[AnalysisProgressContext] Removing batch:', concurrentExecutionId)

      const newActiveBatchId =
        state.activeBatchId === concurrentExecutionId
          ? Object.keys(remainingBatches)[0] || null
          : state.activeBatchId

      return {
        ...state,
        batches: remainingBatches,
        activeBatchId: newActiveBatchId,
        isVisible: Object.keys(remainingBatches).length > 0,
        sideEffects: {
          saveToStorage: true,
          eventsToDispatch: [],
          syncRequests: [],
        },
      }
    }

    case 'SET_VISIBLE': {
      return {
        ...state,
        isVisible: action.payload.visible,
        sideEffects: {
          saveToStorage: false,
          eventsToDispatch: [],
          syncRequests: [],
        },
      }
    }

    case 'SET_ACTIVE_BATCH': {
      return {
        ...state,
        activeBatchId: action.payload.batchId,
        sideEffects: {
          saveToStorage: false,
          eventsToDispatch: [],
          syncRequests: [],
        },
      }
    }

    case 'SYNC_BATCH_STATUS': {
      const { concurrentExecutionId, actualStatus, completedAt } = action.payload
      const batch = state.batches[concurrentExecutionId]

      if (!batch) {
        logger.warn('[AnalysisProgressContext] Batch not found for sync:', concurrentExecutionId)
        return state
      }

      logger.info('[AnalysisProgressContext] Syncing batch status with backend:', {
        concurrentExecutionId,
        currentStatus: batch.status,
        actualStatus,
        completedAt,
      })

      // バッチのステータスを更新
      const updatedBatch: BatchAnalysisProgress = {
        ...batch,
        status: actualStatus,
        ...(completedAt && { completedAt }),
      }

      // ステータスが完了系の場合、matrix内のアイテムも更新
      if (
        actualStatus === 'SUCCEEDED' ||
        actualStatus === 'FAILED' ||
        actualStatus === 'PARTIALLY_SUCCEEDED'
      ) {
        updatedBatch.percentage = 100
        updatedBatch.pendingCount = 0

        // matrix内の未完了アイテムを完了扱いにする
        updatedBatch.matrix = batch.matrix.map(item => {
          if (item.status === 'PENDING' || item.status === 'RUNNING') {
            return {
              ...item,
              status: actualStatus === 'FAILED' ? 'FAILED' : 'SUCCEEDED',
              completedAt: completedAt || new Date(),
            }
          }
          return item
        })

        // 完了数・失敗数を再計算
        updatedBatch.completedCount = updatedBatch.matrix.filter(
          item => item.status === 'SUCCEEDED'
        ).length
        updatedBatch.failedCount = updatedBatch.matrix.filter(
          item => item.status === 'FAILED'
        ).length
      }

      return {
        ...state,
        batches: {
          ...state.batches,
          [concurrentExecutionId]: updatedBatch,
        },
        sideEffects: {
          saveToStorage: true,
          eventsToDispatch: [],
          syncRequests: [],
        },
      }
    }

    case 'CLEAR_SIDE_EFFECTS': {
      return {
        ...state,
        sideEffects: {
          saveToStorage: false,
          eventsToDispatch: [],
          syncRequests: [],
        },
      }
    }

    default:
      return state
  }
}

// Context の値型
export interface AnalysisProgressContextValue {
  state: AnalysisProgressState
  startBatch: (params: {
    concurrentExecutionId: string
    spaceId: string
    serviceId: string
    serviceName: string
    questions: Array<{ text: string; index: number }>
    models: Array<{
      provider: string
      version: string | null
      displayName: string
    }>
    competitorIds?: string[] // 🔧 競合他社IDを追加
    // WHY: UI surfaces large/small route warnings based on startBatch payload.
    routeInfo?: 'small' | 'large'
  }) => void
  updateProgress: (params: {
    concurrentExecutionId: string
    completedCount: number
    failedCount: number
    // 🆕 分析単位のメトリクス（オプショナル）
    analysisMetrics?: {
      totalAnalysesCount: number
      completedAnalysesCount: number
      failedAnalysesCount: number
      pendingAnalysesCount: number
      analysisProgressPercentage: number
    }
  }) => void
  updateMatrixItem: (params: {
    concurrentExecutionId: string
    questionIndex: number
    modelProvider: string
    modelVersion: string | null
    status: AnalysisItemStatus
    error?: string
    executionTimeMs?: number
  }) => void
  completeBatch: (concurrentExecutionId: string) => void
  failBatch: (concurrentExecutionId: string, error: string) => void
  /** 開始が確認できた（started 終局）。「開始確認中」を実行中へ昇格させる（D1-a） */
  confirmBatchStarted: (concurrentExecutionId: string) => void
  /** 開始を確認できなかった（indeterminate 終局）。失敗としては閉じない（D1-a / FR-2-4） */
  markBatchIndeterminate: (concurrentExecutionId: string) => void
  removeBatch: (concurrentExecutionId: string) => void
  setVisible: (visible: boolean) => void
  setActiveBatch: (batchId: string | null) => void
  getActiveBatch: () => BatchAnalysisProgress | null
  getBatch: (concurrentExecutionId: string) => BatchAnalysisProgress | null
  getRunningBatchCount: () => number
  getRunningBatchesForService: (spaceId: string, serviceId: string) => BatchAnalysisProgress[]
  restoreFromStorage: () => void
}

// Context の作成
export const AnalysisProgressContext = createContext<AnalysisProgressContextValue | null>(null)

// Provider コンポーネント
export function AnalysisProgressProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(analysisProgressReducer, getInitialState())
  const { user } = useAuth()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const missingSinceRef = useRef<Record<string, number>>({})
  // WHY: 完了バッチの自動削除タイマーを再レンダーをまたいで永続化する。
  // effectローカルに持つと、無関係な state.batches 更新のたびに clearTimeout でリセットされ、
  // 多重実行時に「正常終了したのに消えない」不具合になる。
  const cleanupTimersRef = useRef<Record<string, NodeJS.Timeout>>({})

  // WebSocket hooks for real-time updates
  const {
    connectionState,
    joinBatch,
    leaveBatch,
    onBatchProgress,
    onBatchCompleted,
    onBatchFailed,
  } = useWebSocket()

  // WebSocket接続状態
  const isWebSocketConnected = connectionState.isConnected

  // 🔧 WebSocket専用モード - 初期化時の同期を無効化
  useEffect(() => {
    const initializeWebSocketOnly = async () => {
      logger.debug('[AnalysisProgressProvider] Component mounted, restoring from localStorage')

      // 初期状態で既に復元されているが、念のため明示的に復元処理を実行
      const restoredBatches = loadBatchProgressFromStorage()

      if (Object.keys(restoredBatches).length > 0) {
        logger.info(
          '[AnalysisProgressProvider] Found active batches after reload (WebSocket-only):',
          {
            batchCount: Object.keys(restoredBatches).length,
            batchIds: Object.keys(restoredBatches),
            note: 'Will rely on WebSocket events for status updates',
          }
        )

        // 明示的にRESTORE_FROM_STORAGEアクションをディスパッチ
        dispatch({
          type: 'RESTORE_FROM_STORAGE',
          payload: { batches: restoredBatches },
        })

        // 🔧 WebSocket専用モード: 初期同期は行わず、WebSocket接続を待つ
        logger.info('[AnalysisProgressProvider] WebSocket-only mode: Skipping initial backend sync')
        logger.info(
          '[AnalysisProgressProvider] Status updates will be handled via WebSocket events only'
        )
      } else {
        logger.debug('[AnalysisProgressProvider] No active batches found in localStorage')
      }
    }

    initializeWebSocketOnly()
  }, [])

  // 🔧 Side effects handler - localStorage save
  useEffect(() => {
    if (state.sideEffects.saveToStorage) {
      saveBatchProgressToStorage(state.batches)
    }
  }, [state.batches, state.sideEffects.saveToStorage])

  // 🔧 Side effects handler - Event dispatching
  useEffect(() => {
    if (state.sideEffects.eventsToDispatch.length > 0) {
      state.sideEffects.eventsToDispatch.forEach(eventConfig => {
        const event = new CustomEvent(eventConfig.type, {
          detail: eventConfig.detail,
        })
        window.dispatchEvent(event)

        logger.info('[AnalysisProgressContext] 📢 Global event dispatched:', {
          eventType: eventConfig.type,
          eventDetail: eventConfig.detail,
        })
      })

      // Clear processed events
      setTimeout(() => {
        dispatch({ type: 'CLEAR_SIDE_EFFECTS' })
      }, 0)
    }
  }, [state.sideEffects.eventsToDispatch])

  // 🔧 WebSocket専用モード - Side effects sync requests を無効化
  useEffect(() => {
    if (state.sideEffects.syncRequests.length > 0) {
      logger.info('[AnalysisProgressContext] WebSocket-only mode: Ignoring sync requests', {
        requestCount: state.sideEffects.syncRequests.length,
        syncRequests: state.sideEffects.syncRequests.map(req => ({
          batchId: req.concurrentExecutionId,
          spaceId: req.spaceId,
          delay: req.delay,
        })),
        note: 'Relying on WebSocket events for real-time updates',
      })

      // Clear sync requests immediately without processing
      dispatch({ type: 'CLEAR_SIDE_EFFECTS' })
    }
  }, [state.sideEffects.syncRequests])

  // 🔧 定期的なクリーンアップと状態確認（60秒おき）
  // WHY: state.batches を依存配列に入れると、WebSocket 進捗更新のたびに setInterval がリセットされ
  // 60秒カウンターが満了しない。batchesRef 経由で最新 state を参照することで interval を安定させる。
  useEffect(() => {
    // useEffect再実行時（WebSocket再接続・ユーザー変更時）にカウンタをリセットし、ポーリングを自動復帰させる
    bulkPollFailureCountRef.current = 0

    const statusCheckInterval = setInterval(async () => {
      const now = new Date()
      const batchesToRemove: string[] = []
      const runningBatches: string[] = []

      Object.entries(batchesRef.current).forEach(([id, batch]) => {
        const hoursSinceStart = (now.getTime() - batch.startedAt.getTime()) / (1000 * 60 * 60)

        // 🔧 強化されたスタックバッチ検出とクリーンアップ
        // 24時間以上経過したバッチは自動削除
        if (hoursSinceStart > 24) {
          batchesToRemove.push(id)
          return
        }

        if (batch.status === 'PENDING' && hoursSinceStart > 2) {
          logger.warn('[AnalysisProgressContext] Removing stuck PENDING batch:', {
            batchId: id,
            status: batch.status,
            serviceName: batch.serviceName,
            hoursSinceStart: Math.round(hoursSinceStart * 100) / 100,
            note: 'PENDING status for >2 hours indicates stuck batch',
          })
          batchesToRemove.push(id)
          return
        }

        // 🔧 ENHANCEMENT: 実行系ステータスのバッチを stale チェック対象に追加
        //
        // CONFIRMING_START（開始確認中）は**意図的に含めない**（D1-a 判断⑨）。
        // 開始が確認できていない実行の進捗を問う意味がなく、確認フェーズと同じ区間に
        // 通信を積み増すことになるため（D4 が避けたい接続の積み増し）。開始の有無の確認は
        // 確認フェーズ（confirm-batch-start）が担う。
        // 一方、同時実行数の計上（getRunningBatchCount）には**逆に含める**（D1-a 判断③）。
        if (
          batch.status === 'RUNNING' ||
          batch.status === 'RERUNNING' ||
          batch.status === 'PENDING'
        ) {
          // Note: The batch status is already verified to be in running states above,
          // so no need for additional terminal state checks
          runningBatches.push(id)

          // 🔧 FIX: 2時間以上前のバッチ（特にPENDING）は確実にスタックしているので強制チェック
          if (hoursSinceStart > 2) {
            logger.warn('[AnalysisProgressContext] Stale batch detected - forcing backend sync:', {
              batchId: id,
              status: batch.status,
              serviceName: batch.serviceName,
              hoursSinceStart: Math.round(hoursSinceStart * 100) / 100,
              note: 'This batch is likely stuck - will force check with backend',
            })
          }
        }
      })

      // 古いバッチを削除
      batchesToRemove.forEach(id => {
        logger.info('[AnalysisProgressContext] Auto-removing expired batch:', id)
        delete missingSinceRef.current[id]
        dispatch({ type: 'REMOVE_BATCH', payload: { concurrentExecutionId: id } })
      })

      // 🔧 FIX: 明示的に失敗済みバッチを除外してログ出力
      const nonFailedBatches = Object.entries(batchesRef.current).filter(
        ([_, batch]) =>
          batch.status !== 'FAILED' &&
          batch.status !== 'CANCELLED' &&
          batch.status !== 'SUCCEEDED' &&
          batch.status !== 'PARTIALLY_SUCCEEDED'
      )
      if (nonFailedBatches.length > 0) {
        logger.debug('[AnalysisProgressContext] Non-failed batches in state:', {
          count: nonFailedBatches.length,
          batches: nonFailedBatches.map(([id, batch]) => ({
            id,
            status: batch.status,
            serviceName: batch.serviceName,
          })),
        })
      }

      // 実行中のバッチのステータスをAPIから確認
      // WebSocket接続の有無に関わらず、定期的にDBと同期
      if (runningBatches.length > 0 && user?.spaces[0]?.id) {
        logger.info('[AnalysisProgressContext] Syncing batch status with database:', {
          batchCount: runningBatches.length,
          batchIds: runningBatches,
          isWebSocketConnected,
          note: 'Only RUNNING/RERUNNING/PENDING batches should be synced',
        })

        const nonTerminalBatches = runningBatches.flatMap(batchId => {
          const batch = batchesRef.current[batchId]
          if (
            !batch ||
            batch.status === 'FAILED' ||
            batch.status === 'CANCELLED' ||
            batch.status === 'SUCCEEDED' ||
            batch.status === 'PARTIALLY_SUCCEEDED'
          )
            return []
          return [batch]
        })

        void (async () => {
          if (bulkPollFailureCountRef.current >= BATCH_CONFIG.maxConsecutivePollFailures) {
            logger.warn(
              '[AnalysisProgressContext] Skipping bulk poll - suspended due to consecutive failures:',
              { failures: bulkPollFailureCountRef.current }
            )
            return
          }

          try {
            const progressResponse = await getBatchProgressBulk(user.spaces[0].id)
            bulkPollFailureCountRef.current = 0
            const progressMap = new Map(
              progressResponse.data.map(progress => [progress.batchId, progress] as const)
            )

            nonTerminalBatches.forEach(batch => {
              const serverProgress = progressMap.get(batch.concurrentExecutionId)

              if (!serverProgress) {
                const missingSince = missingSinceRef.current[batch.concurrentExecutionId]
                if (!missingSince) {
                  const startedMissingAt = Date.now()
                  missingSinceRef.current[batch.concurrentExecutionId] = startedMissingAt

                  logger.info(
                    '[AnalysisProgressContext] Batch missing from active progress response, starting grace period:',
                    {
                      batchId: batch.concurrentExecutionId,
                      status: batch.status,
                      missingSince: new Date(startedMissingAt).toISOString(),
                    }
                  )
                  return
                }

                const missingDurationMs = Date.now() - missingSince
                const shouldKeepLocalBatch =
                  missingDurationMs < BATCH_CONFIG.bulkResponseMissingGracePeriodMs

                if (shouldKeepLocalBatch) {
                  logger.info(
                    '[AnalysisProgressContext] Batch missing from active progress response during grace period, keeping local state:',
                    {
                      batchId: batch.concurrentExecutionId,
                      status: batch.status,
                      missingDurationMs,
                      missingSince: new Date(missingSince).toISOString(),
                    }
                  )
                  return
                }

                logger.info(
                  '[AnalysisProgressContext] Batch missing from active progress response after grace period, removing:',
                  {
                    batchId: batch.concurrentExecutionId,
                    status: batch.status,
                    missingDurationMs,
                    missingSince: new Date(missingSince).toISOString(),
                  }
                )
                delete missingSinceRef.current[batch.concurrentExecutionId]
                toast({
                  title: t('analysis.execution.progress.batchStatusUnavailable', {
                    batchId: `${batch.concurrentExecutionId.slice(0, 8)}...`,
                  }),
                  variant: 'destructive',
                  category: 'batch',
                })
                dispatch({
                  type: 'REMOVE_BATCH',
                  payload: { concurrentExecutionId: batch.concurrentExecutionId },
                })
                return
              }

              delete missingSinceRef.current[batch.concurrentExecutionId]

              const mappedStatus =
                serverProgress.status === 'RERUNNING' ? 'RUNNING' : serverProgress.status

              if (mappedStatus !== batch.status) {
                logger.info('[AnalysisProgressContext] Batch status changed via DB sync:', {
                  batchId: batch.concurrentExecutionId,
                  oldStatus: batch.status,
                  newStatus: mappedStatus,
                })
              }

              if (mappedStatus === 'CANCELLED') {
                dispatch({
                  type: 'CANCEL_BATCH',
                  payload: { concurrentExecutionId: batch.concurrentExecutionId },
                })
                return
              }

              if (isCompletedStatus(mappedStatus as BatchExecutionStatus)) {
                dispatch({
                  type: 'COMPLETE_BATCH',
                  payload: { concurrentExecutionId: batch.concurrentExecutionId },
                })
                return
              }

              dispatch({
                type: 'UPDATE_BATCH_PROGRESS',
                payload: {
                  concurrentExecutionId: batch.concurrentExecutionId,
                  completedCount: serverProgress.progress.completed || 0,
                  failedCount: serverProgress.progress.failed || 0,
                  status: mappedStatus as
                    | 'PENDING'
                    | 'RUNNING'
                    | 'SUCCEEDED'
                    | 'FAILED'
                    | 'PARTIALLY_SUCCEEDED'
                    | undefined,
                  analysisMetrics: serverProgress.analysisMetrics
                    ? {
                        ...serverProgress.analysisMetrics,
                        pendingAnalysesCount:
                          serverProgress.analysisMetrics.totalAnalysesCount -
                          serverProgress.analysisMetrics.completedAnalysesCount -
                          serverProgress.analysisMetrics.failedAnalysesCount,
                        analysisProgressPercentage:
                          serverProgress.analysisMetrics.totalAnalysesCount > 0
                            ? Math.round(
                                (serverProgress.analysisMetrics.completedAnalysesCount /
                                  serverProgress.analysisMetrics.totalAnalysesCount) *
                                  100
                              )
                            : 0,
                      }
                    : undefined,
                },
              })
            })
          } catch (error) {
            bulkPollFailureCountRef.current += 1
            logger.error('[AnalysisProgressContext] Failed to sync bulk batch status:', {
              batchIds: nonTerminalBatches.map(batch => batch.concurrentExecutionId),
              error: error instanceof Error ? error.message : error,
              consecutiveFailures: bulkPollFailureCountRef.current,
            })
            if (bulkPollFailureCountRef.current >= BATCH_CONFIG.maxConsecutivePollFailures) {
              logger.error(
                '[AnalysisProgressContext] Polling suspended due to consecutive failures:',
                { failures: bulkPollFailureCountRef.current }
              )
            }
          }
        })()
      }
    }, BATCH_CONFIG.progressBulkPollingIntervalMs)

    return () => clearInterval(statusCheckInterval)
  }, [isWebSocketConnected, user])

  // 自動クリーンアップ（完了後2秒で削除）
  // NOTE: タイマーは cleanupTimersRef で永続化し、既にスケジュール済みのものは触らない
  //       （＝無関係な state.batches 更新でカウントダウンをリセットしない）。
  useEffect(() => {
    const cleanupTimers = cleanupTimersRef.current

    Object.entries(state.batches).forEach(([id, batch]) => {
      const isTerminal =
        batch.status === 'SUCCEEDED' ||
        batch.status === 'FAILED' ||
        batch.status === 'CANCELLED' ||
        batch.status === 'PARTIALLY_SUCCEEDED'

      if (isTerminal) {
        // 既にスケジュール済みなら再スケジュールしない（タイマーを維持）
        if (!cleanupTimers[id]) {
          cleanupTimers[id] = setTimeout(() => {
            logger.info('[AnalysisProgressContext] 🗑️ Auto-removing completed batch:', {
              batchId: id,
              status: batch.status,
              serviceName: batch.serviceName,
            })
            delete missingSinceRef.current[id]
            dispatch({ type: 'REMOVE_BATCH', payload: { concurrentExecutionId: id } })
            delete cleanupTimers[id]
          }, 2000) // 2秒後に自動削除
        }
      } else if (cleanupTimers[id]) {
        // 実行中に戻った/再実行された場合のみタイマーをクリア
        clearTimeout(cleanupTimers[id])
        delete cleanupTimers[id]
      }
    })

    // 再実行では個々のタイマーを解除しない（維持/解除は上のロジックで判断）。
    // 保留中タイマーの全解除はアンマウント時のみ（下の unmount-only effect）。
  }, [state.batches])

  // アンマウント時に保留中のクリーンアップタイマーを全解除（リーク防止）
  useEffect(() => {
    const timers = cleanupTimersRef.current
    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer))
    }
  }, [])

  // Action creators
  const startBatch = useCallback(
    (params: Parameters<AnalysisProgressContextValue['startBatch']>[0]) => {
      dispatch({ type: 'START_BATCH', payload: params })

      // WebSocketが接続されている場合、バッチルームに参加
      if (connectionState.isConnected) {
        logger.info(
          '[AnalysisProgressContext] Joining new batch room via WebSocket:',
          params.concurrentExecutionId
        )
        joinBatch(params.concurrentExecutionId)
      }
    },
    [connectionState.isConnected, joinBatch]
  )

  const updateProgress = useCallback(
    (params: Parameters<AnalysisProgressContextValue['updateProgress']>[0]) => {
      dispatch({ type: 'UPDATE_BATCH_PROGRESS', payload: params })
    },
    []
  )

  const updateMatrixItem = useCallback(
    (params: Parameters<AnalysisProgressContextValue['updateMatrixItem']>[0]) => {
      dispatch({ type: 'UPDATE_MATRIX_ITEM', payload: params })
    },
    []
  )

  const invalidateBatchQueries = useCallback(
    (
      spaceId: string | undefined,
      serviceId?: string | null,
      options?: { includeExecutionHistory?: boolean }
    ) => {
      if (!spaceId) {
        logger.warn(
          '[AnalysisProgressContext] Skipping cache invalidation - missing spaceId for batch event'
        )
        return
      }

      if (options?.includeExecutionHistory ?? true) {
        queryClient.invalidateQueries({
          queryKey: ['executionHistory', spaceId, serviceId ?? undefined],
        })
      }
      queryClient.invalidateQueries({
        queryKey: ['analysesList', spaceId, serviceId ?? undefined],
      })
      queryClient.invalidateQueries({
        queryKey: ['analysis-usage', spaceId],
      })
      queryClient.invalidateQueries({
        queryKey: ['featureUsage', spaceId, 'ANALYSIS'],
      })

      // Invalidate prompts list with execution counts
      // This ensures the PromptListTable execution count refreshes immediately after analysis
      queryClient.invalidateQueries({
        queryKey: ['promptsWithExecutions', spaceId, serviceId ?? undefined],
      })
    },
    [queryClient]
  )

  const completeBatch = useCallback(
    (concurrentExecutionId: string) => {
      const batch = state.batches[concurrentExecutionId]

      logger.info('[AnalysisProgressContext] 🎯 completeBatch called:', {
        concurrentExecutionId,
        batch: batch
          ? {
              spaceId: batch.spaceId,
              serviceId: batch.serviceId,
              completedCount: batch.completedCount,
              status: batch.status,
            }
          : null,
      })

      // 🔧 Race condition fix: Check if batch is already completed
      if (
        batch &&
        (batch.status === 'SUCCEEDED' ||
          batch.status === 'FAILED' ||
          batch.status === 'CANCELLED' ||
          batch.status === 'PARTIALLY_SUCCEEDED')
      ) {
        logger.warn(
          '[AnalysisProgressContext] Batch already completed - ignoring duplicate completeBatch call:',
          {
            concurrentExecutionId,
            currentStatus: batch.status,
          }
        )
        return
      }

      delete missingSinceRef.current[concurrentExecutionId]
      // Simply dispatch the action - all side effects are now handled by useEffect
      dispatch({ type: 'COMPLETE_BATCH', payload: { concurrentExecutionId } })

      // 🔧 Invalidate query cache to refresh analysis history and usage counts
      if (batch) {
        logger.info(
          '[AnalysisProgressContext] 🔄 Invalidating query cache after batch completion:',
          {
            concurrentExecutionId,
            spaceId: batch.spaceId,
            serviceId: batch.serviceId,
          }
        )

        invalidateBatchQueries(batch.spaceId, batch.serviceId, { includeExecutionHistory: false })
      }
    },
    [state.batches, invalidateBatchQueries]
  )

  const failBatch = useCallback(
    (concurrentExecutionId: string, error: string) => {
      // 🔧 既に失敗状態のバッチへの重複呼び出しを防ぐ
      const currentBatch = state.batches[concurrentExecutionId]
      if (currentBatch?.status === 'FAILED') {
        logger.debug(
          '[AnalysisProgressContext] Batch already failed - skipping duplicate failBatch call:',
          {
            concurrentExecutionId,
            currentError: currentBatch.error,
            newError: error,
          }
        )
        return // 早期リターンで重複処理を防ぐ
      }

      // エラーメッセージのバリデーション
      const errorMessage = error || 'Unknown error occurred'
      logger.warn('[AnalysisProgressContext] failBatch called:', {
        concurrentExecutionId,
        error: errorMessage,
        errorType: typeof error,
        currentBatchStatus: currentBatch?.status,
        stackTrace: new Error().stack,
      })
      delete missingSinceRef.current[concurrentExecutionId]
      dispatch({ type: 'FAIL_BATCH', payload: { concurrentExecutionId, error: errorMessage } })

      // 終局に到達したので進捗イベントの受信経路から離脱する（D1-a）
      if (connectionState.isConnected) {
        leaveBatch(concurrentExecutionId)
      }
    },
    [state.batches, connectionState.isConnected, leaveBatch]
  )

  /**
   * 開始が確認できた（started 終局）
   *
   * 「開始確認中」を実行中へ昇格させる。ここで初めて進捗インジケータに現れる。
   * 受信経路への参加は維持する（以降の進捗を受け続けるため）。D1-a。
   */
  const confirmBatchStarted = useCallback((concurrentExecutionId: string) => {
    dispatch({ type: 'CONFIRM_BATCH_STARTED', payload: { concurrentExecutionId } })
  }, [])

  /**
   * 開始を確認できなかった（indeterminate 終局）
   *
   * 打ち切りとして閉じ、同時実行の計上から外す。**失敗としては閉じない**（FR-2-4）。
   * 受信経路からは離脱する。D1-a。
   */
  const markBatchIndeterminate = useCallback(
    (concurrentExecutionId: string) => {
      delete missingSinceRef.current[concurrentExecutionId]
      dispatch({ type: 'MARK_BATCH_INDETERMINATE', payload: { concurrentExecutionId } })

      if (connectionState.isConnected) {
        leaveBatch(concurrentExecutionId)
      }
    },
    [connectionState.isConnected, leaveBatch]
  )
  const removeBatch = useCallback(
    (concurrentExecutionId: string) => {
      delete missingSinceRef.current[concurrentExecutionId]
      dispatch({ type: 'REMOVE_BATCH', payload: { concurrentExecutionId } })

      // WebSocketが接続されている場合、バッチルームから退出
      if (connectionState.isConnected) {
        logger.info(
          '[AnalysisProgressContext] Leaving batch room via WebSocket:',
          concurrentExecutionId
        )
        leaveBatch(concurrentExecutionId)
      }
    },
    [connectionState.isConnected, leaveBatch]
  )

  const setVisible = useCallback((visible: boolean) => {
    dispatch({ type: 'SET_VISIBLE', payload: { visible } })
  }, [])

  const setActiveBatch = useCallback((batchId: string | null) => {
    dispatch({ type: 'SET_ACTIVE_BATCH', payload: { batchId } })
  }, [])

  const getActiveBatch = useCallback((): BatchAnalysisProgress | null => {
    return state.activeBatchId ? state.batches[state.activeBatchId] || null : null
  }, [state.activeBatchId, state.batches])

  const getBatch = useCallback(
    (concurrentExecutionId: string): BatchAnalysisProgress | null => {
      return state.batches[concurrentExecutionId] || null
    },
    [state.batches]
  )

  const getRunningBatchCount = useCallback((): number => {
    const runningBatches = Object.values(state.batches).filter(batch => {
      // 完了系のステータスは除外
      if (
        batch.status === 'SUCCEEDED' ||
        batch.status === 'FAILED' ||
        batch.status === 'CANCELLED' ||
        batch.status === 'PARTIALLY_SUCCEEDED'
      ) {
        return false
      }
      // 🆕 定期実行は除外（手動実行のみカウント）
      if (batch.jobType === 'SCHEDULED_ANALYSIS') {
        return false
      }
      // CONFIRMING_START を含めるのは意図的（D1-a）。
      // 開始が確認できるまでの実行は画面には出さないが、枠は占有させる。
      // ここから外すと、押下から最大60秒のあいだ同時実行の防御が効かなくなる（D7 / FR-4-4）。
      return (
        batch.status === 'RUNNING' ||
        batch.status === 'PENDING' ||
        batch.status === 'CONFIRMING_START'
      )
    })

    // デバッグログ追加
    if (runningBatches.length > 0) {
      logger.debug('[AnalysisProgressContext] Running batches count (manual only):', {
        count: runningBatches.length,
        batches: runningBatches.map(b => ({
          id: b.concurrentExecutionId,
          status: b.status,
          serviceName: b.serviceName,
          serviceId: b.serviceId,
          spaceId: b.spaceId,
          jobType: b.jobType || 'ANALYSIS', // 🆕 ジョブタイプも表示
        })),
      })
    }

    return runningBatches.length
  }, [state.batches])

  const getRunningBatchesForService = useCallback(
    (spaceId: string, serviceId: string): BatchAnalysisProgress[] => {
      return Object.values(state.batches).filter(batch => {
        if (batch.spaceId !== spaceId || batch.serviceId !== serviceId) {
          return false
        }
        // 完了系のステータスは除外
        if (
          batch.status === 'SUCCEEDED' ||
          batch.status === 'FAILED' ||
          batch.status === 'CANCELLED' ||
          batch.status === 'PARTIALLY_SUCCEEDED'
        ) {
          return false
        }
        return batch.status === 'RUNNING' || batch.status === 'PENDING'
      })
    },
    [state.batches]
  )

  const restoreFromStorage = useCallback(() => {
    const restoredBatches = loadBatchProgressFromStorage()
    dispatch({ type: 'RESTORE_FROM_STORAGE', payload: { batches: restoredBatches } })
  }, [])

  // 🔧 コンポーネントアンマウント時にタイムアウトをクリーンアップ
  useEffect(() => {
    return () => {
      if (saveToStorageTimeoutId) {
        clearTimeout(saveToStorageTimeoutId)
      }
    }
  }, [])

  // Keep a ref to current batches to avoid stale closures
  const batchesRef = useRef(state.batches)
  const bulkPollFailureCountRef = useRef(0)
  useEffect(() => {
    batchesRef.current = state.batches
  }, [state.batches])

  // Keep track of joined batch rooms
  const joinedBatchesRef = useRef<Set<string>>(new Set())

  // WebSocketイベントリスナー
  useEffect(() => {
    if (!connectionState.isConnected) {
      return
    }

    // バッチ進捗イベントのリスナー
    const unsubscribeProgress = onBatchProgress((data: BatchProgressEventData) => {
      logger.info('[AnalysisProgressContext] WebSocket batch progress event received:', data)

      const { batchId, completedCount, totalCount, failedCount, progress } = data

      // 進捗を更新
      dispatch({
        type: 'UPDATE_BATCH_PROGRESS',
        payload: {
          concurrentExecutionId: batchId,
          completedCount: completedCount || 0,
          failedCount: failedCount || 0,
          analysisMetrics: totalCount
            ? {
                totalAnalysesCount: totalCount,
                completedAnalysesCount: completedCount || 0,
                failedAnalysesCount: failedCount || 0,
                pendingAnalysesCount: totalCount - (completedCount || 0) - (failedCount || 0),
                analysisProgressPercentage: progress || 0,
              }
            : undefined,
        },
      })
    })

    // バッチ完了イベントのリスナー
    const unsubscribeCompleted = onBatchCompleted((data: BatchStatusEventData) => {
      logger.info('[AnalysisProgressContext] 🎯 WebSocket batch completed event received:', {
        ...data,
        timestamp: new Date().toISOString(),
        currentBatchStatus: batchesRef.current[data.batchId]?.status,
      })

      if (data.status === 'completed' || data.status === 'partially_succeeded') {
        logger.info('[AnalysisProgressContext] 📨 Dispatching COMPLETE_BATCH action:', {
          batchId: data.batchId,
          status: data.status,
          note: 'Force completing batch based on WebSocket event',
        })
        delete missingSinceRef.current[data.batchId]
        dispatch({
          type: 'COMPLETE_BATCH',
          payload: { concurrentExecutionId: data.batchId },
        })

        const completedBatch = batchesRef.current[data.batchId]
        if (completedBatch) {
          logger.info(
            '[AnalysisProgressContext] 🔄 Invalidating caches from tracked batch completion:',
            {
              batchId: data.batchId,
              spaceId: completedBatch.spaceId,
              serviceId: completedBatch.serviceId,
            }
          )
          invalidateBatchQueries(completedBatch.spaceId, completedBatch.serviceId, {
            includeExecutionHistory: false,
          })
          return
        }

        logger.info(
          '[AnalysisProgressContext] 🛰️ WebSocket completion received without local batch state:',
          {
            batchId: data.batchId,
            status: data.status,
            spaceId: data.spaceId,
            serviceId: data.serviceId,
          }
        )

        invalidateBatchQueries(data.spaceId, data.serviceId ?? null, {
          includeExecutionHistory: false,
        })

        if (data.spaceId && data.serviceId) {
          const fallbackStatus =
            data.status === 'partially_succeeded'
              ? ('PARTIALLY_SUCCEEDED' as const)
              : ('SUCCEEDED' as const)

          const fallbackEvent = new CustomEvent('batchAnalysisCompleted', {
            detail: {
              batchId: data.batchId,
              spaceId: data.spaceId,
              serviceId: data.serviceId,
              status: fallbackStatus,
              resultsCount: undefined,
              timestamp: new Date().toISOString(),
            },
          })
          window.dispatchEvent(fallbackEvent)

          logger.info(
            '[AnalysisProgressContext] 📢 Dispatched fallback batchAnalysisCompleted event for Raw Data refresh',
            {
              batchId: data.batchId,
              mappedStatus: fallbackStatus,
            }
          )
        } else {
          logger.warn(
            '[AnalysisProgressContext] Skipped fallback batchAnalysisCompleted event due to missing identifiers',
            {
              batchId: data.batchId,
              hasSpaceId: !!data.spaceId,
              hasServiceId: !!data.serviceId,
            }
          )
        }
      }
    })

    // バッチ失敗イベントのリスナー
    const unsubscribeFailed = onBatchFailed((data: BatchStatusEventData) => {
      logger.info('[AnalysisProgressContext] WebSocket batch failed event received:', data)
      delete missingSinceRef.current[data.batchId]

      if (data.status === 'cancelled') {
        dispatch({
          type: 'CANCEL_BATCH',
          payload: { concurrentExecutionId: data.batchId },
        })
      } else {
        dispatch({
          type: 'FAIL_BATCH',
          payload: {
            concurrentExecutionId: data.batchId,
            error: data.error || 'Batch failed',
          },
        })
      }

      const failedBatch = batchesRef.current[data.batchId]
      if (failedBatch) {
        invalidateBatchQueries(failedBatch.spaceId, failedBatch.serviceId, {
          includeExecutionHistory: false,
        })
      } else {
        invalidateBatchQueries(data.spaceId, data.serviceId ?? null, {
          includeExecutionHistory: false,
        })

        if (data.spaceId && data.serviceId) {
          const fallbackEvent = new CustomEvent('batchAnalysisCompleted', {
            detail: {
              batchId: data.batchId,
              spaceId: data.spaceId,
              serviceId: data.serviceId,
              status: 'FAILED',
              resultsCount: undefined,
              timestamp: new Date().toISOString(),
            },
          })
          window.dispatchEvent(fallbackEvent)

          logger.info(
            '[AnalysisProgressContext] 📢 Dispatched fallback failure event for Raw Data refresh',
            {
              batchId: data.batchId,
            }
          )
        } else {
          logger.warn(
            '[AnalysisProgressContext] Failed to dispatch fallback failure event - missing identifiers',
            {
              batchId: data.batchId,
              hasSpaceId: !!data.spaceId,
              hasServiceId: !!data.serviceId,
            }
          )
        }
      }
    })

    // クリーンアップ
    return () => {
      unsubscribeProgress()
      unsubscribeCompleted()
      unsubscribeFailed()
    }
  }, [
    connectionState.isConnected,
    onBatchProgress,
    onBatchCompleted,
    onBatchFailed,
    invalidateBatchQueries,
  ])

  // Separate effect for managing batch room subscriptions
  useEffect(() => {
    // WebSocketが完全に接続されていない場合は処理をスキップ
    if (!connectionState.isConnected || connectionState.isConnecting) {
      logger.debug('[AnalysisProgressContext] WebSocket not ready for batch room management:', {
        isConnected: connectionState.isConnected,
        isConnecting: connectionState.isConnecting,
        error: connectionState.error?.message,
      })
      return
    }

    // WebSocket接続後に少し待つ（接続直後の不安定な状態を避ける）
    const connectionDelay = setTimeout(() => {
      // Join new batches and leave completed ones
      const currentBatchIds = Object.keys(state.batches)
      const runningBatches = currentBatchIds.filter(batchId => {
        const batch = state.batches[batchId]
        return (
          batch.status === 'RUNNING' || batch.status === 'PENDING' || batch.status === 'RERUNNING'
        )
      })

      // Join new batch rooms
      runningBatches.forEach(batchId => {
        if (!joinedBatchesRef.current.has(batchId)) {
          logger.info('[AnalysisProgressContext] Joining batch room via WebSocket:', batchId)
          try {
            joinBatch(batchId)
            joinedBatchesRef.current.add(batchId)
          } catch (error) {
            logger.error('[AnalysisProgressContext] Failed to join batch room:', {
              batchId,
              error: error instanceof Error ? error.message : error,
            })
          }
        }
      })

      // Leave completed batch rooms
      Array.from(joinedBatchesRef.current).forEach(batchId => {
        if (!runningBatches.includes(batchId)) {
          logger.info('[AnalysisProgressContext] Leaving batch room via WebSocket:', batchId)
          try {
            leaveBatch(batchId)
            joinedBatchesRef.current.delete(batchId)
          } catch (error) {
            logger.error('[AnalysisProgressContext] Failed to leave batch room:', {
              batchId,
              error: error instanceof Error ? error.message : error,
            })
            // エラーが発生しても追跡から削除
            joinedBatchesRef.current.delete(batchId)
          }
        }
      })
    }, 500) // 500ms待つ

    return () => {
      clearTimeout(connectionDelay)
    }
  }, [
    connectionState.isConnected,
    connectionState.isConnecting,
    state.batches,
    joinBatch,
    leaveBatch,
  ])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Leave all joined batch rooms on unmount
      joinedBatchesRef.current.forEach((batchId: string) => {
        leaveBatch(batchId)
      })
      joinedBatchesRef.current.clear()
    }
  }, [leaveBatch])
  const contextValue: AnalysisProgressContextValue = {
    state,
    startBatch,
    updateProgress,
    updateMatrixItem,
    completeBatch,
    failBatch,
    confirmBatchStarted,
    markBatchIndeterminate,
    removeBatch,
    setVisible,
    setActiveBatch,
    getActiveBatch,
    getBatch,
    getRunningBatchCount,
    getRunningBatchesForService,
    restoreFromStorage,
  }

  return (
    <AnalysisProgressContext.Provider value={contextValue}>
      {children}
    </AnalysisProgressContext.Provider>
  )
}

export default AnalysisProgressProvider
